// frontend/src/services/api.js
import axios from 'axios';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add request timestamp for caching
    config.headers['X-Request-Timestamp'] = Date.now();
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => {
    // Handle token refresh
    const refreshedToken = response.headers['x-refreshed-token'];
    if (refreshedToken) {
      localStorage.setItem('token', refreshedToken);
    }
    
    // Return simplified response
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 - Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(
            `${import.meta.env.VITE_API_URL || '/api'}/auth/refresh`,
            { refreshToken }
          );
          
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
            return apiClient(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // Handle other errors
    const errorData = error.response?.data || {
      error: 'Network Error',
      message: 'Unable to connect to the server'
    };
    
    return Promise.reject(errorData);
  }
);

// API methods
export const api = {
  // Generic HTTP methods
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
  
  // Auth endpoints
  auth: {
    login: (credentials) => apiClient.post('/auth/login', credentials),
    register: (userData) => apiClient.post('/auth/register', userData),
    logout: () => apiClient.post('/auth/logout'),
    verify: () => apiClient.get('/auth/verify'),
    forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
    resetPassword: (token, password) => apiClient.post('/auth/reset-password', { token, password }),
  },
  
  // User endpoints
  users: {
    getProfile: () => apiClient.get('/auth/profile'),
    updateProfile: (data) => apiClient.put('/auth/profile', data),
    changePassword: (data) => apiClient.put('/auth/change-password', data),
  },
  
  // Coach endpoints
  coaches: {
    getAll: (params) => apiClient.get('/coaches', { params }),
    getById: (id) => apiClient.get(`/coaches/${id}`),
    getProfile: (coachId) => apiClient.get(`/coaches/profile?coachId=${coachId}`),
    updateProfile: (data) => apiClient.put('/coaches/profile', data),
    getOfferings: (coachId, params) => apiClient.get(`/coaches/offerings?coachId=${coachId}`, { params }),
    getStats: () => apiClient.get('/coaches/stats'),
    getBookings: (params) => apiClient.get('/coaches/bookings', { params }),
  },
  
  // Offering endpoints
  offerings: {
    getAll: (params) => apiClient.get('/offerings', { params }),
    getById: (id) => apiClient.get(`/offerings/${id}`),
    create: (data) => apiClient.post('/offerings', data),
    update: (id, data) => apiClient.put(`/offerings/${id}`, data),
    delete: (id) => apiClient.delete(`/offerings/${id}`),
    toggleStatus: (id, isActive) => apiClient.patch(`/offerings/${id}`, { is_active: isActive }),
  },
  
  // Sports endpoints
  sports: {
    getAll: () => apiClient.get('/sports'),
    getById: (id) => apiClient.get(`/sports/${id}`),
    search: (query) => apiClient.get(`/sports/search?q=${query}`),
  },
  
  // Facilities endpoints
  facilities: {
    getAll: (params) => apiClient.get('/facilities', { params }),
    getById: (id) => apiClient.get(`/facilities/${id}`),
    create: (data) => apiClient.post('/facilities', data),
    update: (id, data) => apiClient.put(`/facilities/${id}`, data),
  },
  
  // Booking endpoints
  bookings: {
    getAll: (params) => apiClient.get('/bookings', { params }),
    getById: (id) => apiClient.get(`/bookings/${id}`),
    create: (data) => apiClient.post('/bookings', data),
    update: (id, data) => apiClient.put(`/bookings/${id}`, data),
    cancel: (id, reason) => apiClient.post(`/bookings/${id}/cancel`, { reason }),
  },
  
  // Review endpoints
  reviews: {
    create: (data) => apiClient.post('/reviews', data),
    update: (id, data) => apiClient.put(`/reviews/${id}`, data),
    delete: (id) => apiClient.delete(`/reviews/${id}`),
    report: (id, reason) => apiClient.post(`/reviews/${id}/report`, { reason }),
  },
  
  // Upload endpoints
  upload: {
    avatar: (file) => {
      const formData = new FormData();
      formData.append('avatar', file);
      return apiClient.post('/upload/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    document: (file, type) => {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('type', type);
      return apiClient.post('/upload/document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
  },
  
  // Cache utility methods
  cache: {
    set: (key, data, ttl = 300) => {
      const item = {
        data,
        expiry: Date.now() + ttl * 1000,
      };
      localStorage.setItem(`cache_${key}`, JSON.stringify(item));
    },
    
    get: (key) => {
      const itemStr = localStorage.getItem(`cache_${key}`);
      if (!itemStr) return null;
      
      const item = JSON.parse(itemStr);
      if (Date.now() > item.expiry) {
        localStorage.removeItem(`cache_${key}`);
        return null;
      }
      
      return item.data;
    },
    
    clear: (key) => {
      localStorage.removeItem(`cache_${key}`);
    },
    
    clearAll: () => {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('cache_')) {
          localStorage.removeItem(key);
        }
      });
    },
  },
};

export default apiClient;